import { createApi } from '@reduxjs/toolkit/query/react'
import type {
	P2pActivitiesApiResponse,
	P2pActivitiesRequest,
	P2pHoldersApiResponse,
	P2pHoldersRequest,
	P2pBuyApiResponse,
	P2pBuyRequest,
	P2PMarketDetailApiResponse,
	P2PMarketPercentsRequest,
	P2PMarketPercentsApiResponse,
	P2PMarketsApiResponse,
	P2PMarketsRequest,
} from '@/types/p2p'
import { playerBaseQuery } from './playerBaseQuery'

export const p2pApi = createApi({
	reducerPath: 'p2pApi',
	baseQuery: playerBaseQuery,
	tagTypes: ['P2PMarkets', 'P2PMarketDetail', 'P2PMarketPercents', 'P2PHolders', 'P2PActivities'],
	endpoints: (builder) => ({
		getP2pMarkets: builder.query<P2PMarketsApiResponse, P2PMarketsRequest>({
			query: (params) => ({
				url: '/p2p/markets',
				params,
			}),
			providesTags: ['P2PMarkets'],
		}),
		getP2pMarketDetail: builder.query<P2PMarketDetailApiResponse, string>({
			query: (id) => `/p2p/market/${id}`,
			providesTags: (_result, _error, id) => [{ type: 'P2PMarketDetail', id }],
		}),
		getP2pMarketPercents: builder.query<P2PMarketPercentsApiResponse, P2PMarketPercentsRequest>({
			query: ({ market_id, duration }) => ({
				url: '/p2p/percents',
				params: { market_id, duration },
			}),
			providesTags: (_result, _error, { market_id }) => [{ type: 'P2PMarketPercents', id: market_id }],
		}),
		getP2pHolders: builder.query<P2pHoldersApiResponse, P2pHoldersRequest>({
			query: (params) => ({
				url: '/history/p2p/holders',
				params,
			}),
			providesTags: (_result, _error, { market_id, option_id }) => [{ type: 'P2PHolders', id: `${market_id}:${option_id}` }],
		}),
		getP2pActivities: builder.query<P2pActivitiesApiResponse, P2pActivitiesRequest>({
			query: (params) => ({
				url: '/history/p2p/activities',
				params,
			}),
			providesTags: (_result, _error, { market_id, option_id }) => [{ type: 'P2PActivities', id: `${market_id}:${option_id}` }],
		}),
		buyP2pMarket: builder.mutation<P2pBuyApiResponse, P2pBuyRequest>({
			query: (body) => ({
				url: '/p2p/buy',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				'P2PMarkets',
				{ type: 'P2PMarketPercents', id: arg.market_id },
				{ type: 'P2PHolders', id: `${arg.market_id}:${arg.option_id}` },
				{ type: 'P2PActivities', id: `${arg.market_id}:${arg.option_id}` },
			],
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					const { savedMarket } = data.data

					dispatch(
						p2pApi.util.updateQueryData('getP2pMarketDetail', arg.market_id, (draft) => {
							draft.data = savedMarket
						}),
					)
				} catch {
					// Error toast is handled by playerBaseQuery.
				}
			},
		}),
	}),
})

export const { useGetP2pMarketsQuery, useGetP2pMarketDetailQuery, useGetP2pMarketPercentsQuery, useGetP2pHoldersQuery, useGetP2pActivitiesQuery, useBuyP2pMarketMutation } = p2pApi
